// Detailed content for each packaged platform. One record per slug, loaded by
// the PlatformDetailPage component at /platforms/:slug.

export const PLATFORM_DETAILS = {
  "medicaid-case-management": {
    slug: "medicaid-case-management",
    code: "P01",
    tag: "Case Ops",
    category: "Case Management · Public Programs",
    name: "Medicaid Case Management Platform",
    headline: "Case intake to adjudication in a single, control-mapped workspace.",
    description:
      "A modular case management system originally built for state Medicaid but readily configured for adjacent public programs — child welfare, SNAP/TANF, and broader human services. Every case moves through configurable workflows with SLA tracking, worker load balancing, and a complete audit trail.",
    sectors: ["Health & Medicaid", "Human Services", "Child Welfare"],
    keyStats: [
      { label: "Cases / month capacity", value: "250K+" },
      { label: "Median case cycle", value: "-38%" },
      { label: "Concurrent workers", value: "2,400" },
      { label: "SLA compliance", value: "99.6%" },
    ],
    modules: [
      { icon: "FilePlus2", name: "Intake & Application", description: "Multi-channel intake (web, phone, paper) with structured data capture and duplicate-suspect detection." },
      { icon: "ShieldCheck", name: "Eligibility Determination", description: "Rule-driven eligibility engine mapped to program policy with versioned rulebooks and traceable decisions." },
      { icon: "Gavel", name: "Adjudication", description: "Case adjudication with worker queues, load balancing, and quality assurance sampling." },
      { icon: "Scale", name: "Appeals & Fair Hearings", description: "Full appeals lifecycle with document management, hearing scheduling, and disposition tracking." },
      { icon: "Repeat", name: "Renewals & Redeterminations", description: "Automated renewal workflows with ex-parte data validation and citizen-friendly notices." },
      { icon: "ClipboardCheck", name: "Quality Assurance", description: "Sampling-based QA with corrective action tracking and audit-ready reporting." },
    ],
    features: [
      "FHIR R4 record model with USCDI v3 (health deployments)",
      "Program-agnostic case schema for non-health workloads",
      "Configurable SLA rules per case type",
      "Worker load balancing algorithms",
      "Real-time case status dashboards",
      "Multi-language citizen notices",
      "Immutable audit trail for every decision",
      "Role-based permissions with sensitivity tags",
    ],
    integrations: ["State MMIS", "CMS T-MSIS", "State HIE (HL7 / FHIR)", "IRS income verification", "SSA data exchange", "Vital records", "Amazon Connect", "Genesys Cloud"],
    compliance: ["HIPAA / HITECH", "42 CFR Part 2", "NIST SP 800-53 Rev.5", "MARS-E 2.2", "State policy manuals"],
    useCases: [
      "State Medicaid agency case modernization",
      "Child welfare / DFS case tracking",
      "SNAP / TANF human-services case ops",
      "State Health Benefit Exchange operations",
      "General public-program adjudication",
    ],
    engagement:
      "Fixed-fee delivery in four-phase waves (Assess → Design → Migrate → Validate) with continuous ATO documentation. Base package deployable in 8 weeks; full state rollout typically 6–9 months.",
  },

  "ai-intake-automation": {
    slug: "ai-intake-automation",
    code: "P02",
    tag: "AI · Ingestion",
    category: "AI Automation · Cross-Sector",
    name: "AI Intake Automation",
    headline: "Structured intake at machine speed — with a human where it matters.",
    description:
      "An LLM-powered intake layer that captures information from web forms, phone calls, documents, and email, then delivers a structured, PII-redacted record to your downstream system. Human-in-loop review gates prevent silent failures. Deployed across health, human services, judiciary, revenue, safety, and education intake channels.",
    sectors: ["Health & Medicaid", "Human Services", "Public Safety", "Judiciary", "Revenue & DMV", "Education"],
    keyStats: [
      { label: "Manual entry reduction", value: "70%+" },
      { label: "Median ingest time", value: "8.2 s" },
      { label: "Redaction accuracy", value: "99.4%" },
      { label: "Channels supported", value: "6" },
    ],
    modules: [
      { icon: "Mic", name: "Voice Ingestion", description: "Real-time transcription with structured entity extraction for phone-based intake." },
      { icon: "FileText", name: "Document Ingestion", description: "OCR + LLM classification for uploaded PDFs, images, and legacy paper scans." },
      { icon: "MessagesSquare", name: "Web & Chat", description: "Conversational intake surfaces with fallback to human agent handoff." },
      { icon: "Shield", name: "PII Redaction", description: "Deterministic + ML-based redaction of SSN, DOB, financial identifiers before storage." },
      { icon: "UserCheck", name: "Human-in-Loop Review", description: "Configurable confidence thresholds route ambiguous cases to trained reviewers." },
      { icon: "Route", name: "Downstream Routing", description: "Structured output pushed to your case management, court, DMV, or ERP system via API or event bus." },
    ],
    features: [
      "GPT-class LLM with private inference option",
      "Confidence-scored extraction with review threshold",
      "Multi-language (English, Spanish, Haitian Creole, Vietnamese)",
      "Deterministic PII redaction + ML backup",
      "Adjustable HIL confidence cutoff",
      "Full prompt + response audit trail",
      "SFTP, REST, and event-bus outputs",
      "SLA monitoring and reprocessing queues",
    ],
    integrations: ["Amazon Connect", "Genesys", "Twilio", "Salesforce", "DocuSign", "MMIS", "Court case management", "DMV / RMV back-ends", "Any FHIR / REST system"],
    compliance: ["HIPAA / HITECH", "SOC 2 Type II", "NIST SP 800-53 Rev.5", "NIST AI RMF"],
    useCases: [
      "Medicaid / SNAP / TANF application intake",
      "Court e-filing intake and classification",
      "DMV / RMV form and title intake",
      "Public-safety tip line and complaint intake",
      "Higher-ed FAFSA and financial-aid intake",
    ],
    engagement:
      "Fixed-fee proof-of-value in 6 weeks against one channel and one case type, then production-hardening in additional 8–12 week increments.",
  },

  "provider-portal": {
    slug: "provider-portal",
    code: "P03",
    tag: "Self-service",
    category: "Provider & Vendor Self-Service",
    name: "Provider Portal",
    headline: "One SSO surface for the entire provider or vendor lifecycle.",
    description:
      "A federated self-service portal originally built for state Medicaid provider networks, now deployed for state vendor management, contractor onboarding, and school-district supplier portals. Covers enrollment, credentialing, status inquiry, and pre-authorization flows with delegated administration.",
    sectors: ["Health & Medicaid", "Human Services", "Revenue & DMV", "Education"],
    keyStats: [
      { label: "Providers / vendors", value: "40K+" },
      { label: "Login → task complete", value: "-52%" },
      { label: "Prior-auth turnaround", value: "-64%" },
      { label: "Mobile session share", value: "38%" },
    ],
    modules: [
      { icon: "UserPlus", name: "Enrollment", description: "Full enrollment workflow with document upload, primary source verification, and program-specific validations." },
      { icon: "BadgeCheck", name: "Credentialing", description: "Primary source verification against DEA, NPI, board certifications, and license boards with rev-cycle tracking." },
      { icon: "Receipt", name: "Status Inquiry", description: "Near-real-time claims, invoice, or task status with denial code decoding and remediation guidance." },
      { icon: "ClipboardList", name: "Pre-Authorization", description: "Prior-auth or approval submission with attached documentation and status pings." },
      { icon: "Bell", name: "Notifications", description: "Multi-channel notifications (email, SMS, portal inbox) with delivery guarantees." },
      { icon: "Users", name: "Delegated Admin", description: "Group-level administrators can manage staff access without agency involvement." },
    ],
    features: [
      "SAML 2.0 / OIDC federated identity",
      "MFA + passkey support",
      "Delegated administration",
      "Mobile-responsive UI (WCAG 2.2 AA)",
      "Structured audit log per user action",
      "In-portal messaging with agency staff",
      "Downloadable EOB, remittance, or invoice",
      "REST API for large-group EDI clients",
    ],
    integrations: ["State IAM", "NPI Registry", "DEA verification", "Primary source license boards", "MMIS", "State ERP / procurement", "SIS (education)"],
    compliance: ["HIPAA / HITECH (health tenants)", "NIST SP 800-63 (AAL2 / IAL2)", "NIST SP 800-53 Rev.5", "FERPA (education tenants)"],
    useCases: [
      "State Medicaid provider network operations",
      "MCO provider self-service",
      "State vendor / contractor management",
      "School-district supplier portals",
      "Multi-payer credentialing platforms",
    ],
    engagement:
      "Fixed-fee delivery of a base portal in 10–14 weeks, with additional modules added on quarterly release trains.",
  },

  "claims-analytics": {
    slug: "claims-analytics",
    code: "P04",
    tag: "BI · FWA",
    category: "Analytics · Payment Integrity",
    name: "Claims Analytics",
    headline: "Every claim, measured. Every anomaly, surfaced.",
    description:
      "A governed lakehouse plus dashboards for any high-volume claims workflow — Medicaid claims, unemployment insurance, workers-compensation, and tax-refund fraud detection. FWA models are transparent — every flagged item comes with an explanation and a workflow to disposition it.",
    sectors: ["Health & Medicaid", "Human Services", "Revenue & DMV"],
    keyStats: [
      { label: "Claims / day capacity", value: "20M" },
      { label: "FWA precision (val.)", value: "0.87" },
      { label: "Dashboard latency", value: "< 2 min" },
      { label: "Avg. savings / audit", value: "$412K" },
    ],
    modules: [
      { icon: "Database", name: "Claims Lakehouse", description: "Ingest claims and encounters into a Delta / Iceberg lakehouse with row-level governance." },
      { icon: "AlertTriangle", name: "FWA Detection", description: "Explainable ML models for pattern-based anomaly detection with confidence and drivers." },
      { icon: "BarChart3", name: "Executive Dashboards", description: "Agency-facing dashboards on program spend, utilization, and network adequacy." },
      { icon: "PieChart", name: "Entity Analytics", description: "Provider, employer, or filer level utilization, cost, and outcome scoring with peer benchmarking." },
      { icon: "Network", name: "Network Adequacy", description: "GIS-based network adequacy reports required for CMS reporting and MCO oversight." },
      { icon: "FileDown", name: "Report Distribution", description: "Scheduled, signed report distribution with recipient audit trail." },
    ],
    features: [
      "Delta Lake or Iceberg table format",
      "Row-level and column-level access control",
      "Explainable FWA model outputs",
      "Peer-group benchmarking",
      "GIS-based network adequacy",
      "Scheduled + on-demand report distribution",
      "Direct connection from Excel + Tableau + Power BI",
      "Model card + governance registry",
    ],
    integrations: ["MMIS", "MCO 837 feeds", "PBM claims", "State unemployment insurance systems", "Workers-comp administrators", "Revenue tax systems", "Tableau / Power BI / Excel"],
    compliance: ["HIPAA / HITECH", "HITECH breach notification", "NIST SP 800-53 Rev.5", "MITA 3.0 alignment"],
    useCases: [
      "Payment integrity for state Medicaid",
      "Unemployment insurance FWA detection",
      "Workers-compensation claims analytics",
      "State tax refund fraud detection",
      "MCO oversight and performance",
    ],
    engagement:
      "First lakehouse and 3 dashboards live in 10 weeks. FWA models tuned on program-specific data in the following 12 weeks with 3PAO-style validation.",
  },

  "healthcare-workflow-automation": {
    slug: "healthcare-workflow-automation",
    code: "P05",
    tag: "Automation",
    category: "Workflow · Cross-Sector Automation",
    name: "Healthcare Workflow Automation",
    headline: "Move the paperwork. Not the people.",
    description:
      "Bot orchestration and workflow engines that automate the repeatable tasks that drown staff — originally healthcare (insurance verification, referral routing, prior-auth support, clinical documentation), and now extended to human-services caseworker docs, court clerk workflows, and back-office finance. Human-in-loop gates keep decision-makers in control.",
    sectors: ["Health & Medicaid", "Human Services", "Judiciary", "Revenue & DMV"],
    keyStats: [
      { label: "Manual-hours saved / mo", value: "8,400" },
      { label: "Referral / routing cycle", value: "-71%" },
      { label: "Verification accuracy", value: "99.1%" },
      { label: "Bots deployed", value: "22 avg." },
    ],
    modules: [
      { icon: "ShieldCheck", name: "Insurance / Eligibility Verification", description: "Real-time checks against payers or benefit systems, with fallback rules for edge cases." },
      { icon: "GitBranch", name: "Referral / Case Routing", description: "Rule-based routing of referrals or case work across networks with pre-flight checks." },
      { icon: "FileCheck2", name: "Document Prep", description: "Automated packaging of evidence for prior-auth, appeals, or hearings." },
      { icon: "Bot", name: "RPA / API Bots", description: "Mix of API-native and screen-scraping bots for legacy systems without APIs." },
      { icon: "AlertTriangle", name: "Exception Handling", description: "Every failed bot run surfaces to a triage queue with runbook context." },
      { icon: "Users", name: "Human-in-Loop", description: "Configurable gates ensure clinicians, caseworkers, or clerks approve any decision that matters." },
    ],
    features: [
      "Bot library covering top 40 payer sites",
      "Cross-sector bots (court clerk, DMV back-office, HR)",
      "API-first automations where available",
      "Screen-scraping fallback with change detection",
      "Exception triage with runbook attachment",
      "Full audit trail per bot run",
      "SLA + performance dashboards",
      "Rollback per automation, per tenant",
    ],
    integrations: ["Epic / Cerner / Allscripts", "Availity", "Change Healthcare", "Court case management systems", "State ERP / finance", "Fax gateways"],
    compliance: ["HIPAA / HITECH", "42 CFR Part 2", "NIST SP 800-53 Rev.5"],
    useCases: [
      "State-run clinics and FQHCs",
      "Human-services caseworker document automation",
      "Court clerk filing and docket workflows",
      "Back-office finance and reconciliation",
      "MCO clinical operations",
    ],
    engagement:
      "Discovery + top-3 automations delivered in 6 weeks. Additional automations on 4-week release cadence.",
  },

  "hipaa-document-management": {
    slug: "hipaa-document-management",
    code: "P06",
    tag: "Records",
    category: "Records · Multi-Framework Compliance",
    name: "HIPAA Document Management",
    headline: "Records with a chain of custody strong enough to defend.",
    description:
      "An encrypted document store with retention policies, e-signature, chain-of-custody logging, and DTI-approved data classification tagging. HIPAA is the strictest control profile — the platform also runs under CJIS, FERPA, and general public-records regimes for judiciary, education, law enforcement, and revenue archives.",
    sectors: ["Health & Medicaid", "Judiciary", "Human Services", "Public Safety", "Education", "Revenue & DMV"],
    keyStats: [
      { label: "Documents managed", value: "180M+" },
      { label: "Ingest peak", value: "12K / hr" },
      { label: "Search P95 latency", value: "180 ms" },
      { label: "Retention rules", value: "Unlimited" },
    ],
    modules: [
      { icon: "Upload", name: "Ingestion", description: "Multi-channel ingest (upload, watched folder, email, scanner, fax) with classification on the way in." },
      { icon: "Lock", name: "Encrypted Storage", description: "AES-256 at rest, TLS 1.3 in transit, customer-managed keys via KMS / HSM." },
      { icon: "Signature", name: "E-Signature", description: "In-line e-signature with per-signer audit trail — compatible with DocuSign and Adobe Sign." },
      { icon: "History", name: "Retention & Holds", description: "Automated retention scheduling with legal-hold overrides and defensible deletion." },
      { icon: "Search", name: "Search & Discovery", description: "Full-text search across OCR'd content with role-scoped result surfacing." },
      { icon: "Tags", name: "Classification", description: "Automatic + rules-based tagging aligned to DTI, FERPA, CJIS, or HIPAA classification levels." },
    ],
    features: [
      "AES-256 at rest with CMKs",
      "TLS 1.3 in transit",
      "Immutable audit log",
      "OCR on ingest with searchable text layer",
      "Retention policy engine",
      "Litigation hold with override audit",
      "Chain-of-custody API",
      "S3-compatible export",
    ],
    integrations: ["DocuSign", "Adobe Acrobat Sign", "Microsoft 365", "Google Workspace", "SharePoint", "Court case management", "SIS / LMS", "MFT / SFTP"],
    compliance: ["HIPAA / HITECH", "21 CFR Part 11", "CJIS Security Policy", "FERPA", "NIST SP 800-53 Rev.5", "DE DTI classification", "FRCP-defensible"],
    useCases: [
      "Agency records management (any sector)",
      "Court case records and judiciary archives",
      "Law-enforcement evidence chain of custody",
      "K-12 / higher-ed student records (FERPA)",
      "FOIA / public-records response",
    ],
    engagement:
      "Fixed-fee stand-up in 6 weeks with content migration in additional 4–8 week waves depending on source system complexity.",
  },

  "digital-forms": {
    slug: "digital-forms",
    code: "P07",
    tag: "No-code",
    category: "Citizen Digital · Accessibility",
    name: "Digital Forms Platform",
    headline: "Every state form. Every language. Every accessibility standard.",
    description:
      "A no-code form builder for public sector programs with WCAG 2.2 AA compliance, multi-language runtime, offline collection, and integrated e-signature. Deployed across health, human services, environment, revenue, judiciary, education, and defense forms — program staff can publish new forms in hours, not months.",
    sectors: ["Health & Medicaid", "Human Services", "Public Safety", "Judiciary", "Revenue & DMV", "Education", "Environment", "Defense · DoD"],
    keyStats: [
      { label: "Forms in production", value: "1,200+" },
      { label: "Avg. build time", value: "3.4 hrs" },
      { label: "Accessibility score", value: "100% AA" },
      { label: "Languages supported", value: "12" },
    ],
    modules: [
      { icon: "PenLine", name: "Form Builder", description: "Drag-and-drop composer with conditional logic, calculations, and multi-page flows." },
      { icon: "Globe", name: "Multi-Language Runtime", description: "Publish once, deliver in any supported language with per-locale review workflow." },
      { icon: "Accessibility", name: "Accessibility Engine", description: "Auto-lint against WCAG 2.2 AA — no form ships that fails audit." },
      { icon: "Signature", name: "E-Signature", description: "Built-in e-signature with per-signer identity binding." },
      { icon: "WifiOff", name: "Offline Collection", description: "Progressive Web App runtime works offline for field workers." },
      { icon: "BarChart3", name: "Analytics", description: "Submission funnel analytics, drop-off heatmaps, completion times." },
    ],
    features: [
      "WCAG 2.2 AA + ADA Section 508 compliant",
      "Multi-language runtime with translation memory",
      "Offline-capable PWA delivery",
      "Conditional logic + calculations",
      "Built-in e-signature",
      "Submission funnel analytics",
      "Version control per form",
      "Publish approval workflows",
    ],
    integrations: ["Case management systems", "MMIS", "SharePoint", "Salesforce", "DocuSign / Adobe Sign", "Payment gateways (Stripe, Nelnet)", "Notification services"],
    compliance: ["HIPAA / HITECH", "ADA Section 508", "WCAG 2.2 AA", "FERPA", "NIST SP 800-53 Rev.5"],
    useCases: [
      "State agency intake modernization",
      "Environmental permits and inspections",
      "Grant application platforms",
      "K-12 / higher-ed enrollment forms",
      "Field-worker data collection (public health, environment)",
    ],
    engagement:
      "Platform available on subscription with fixed-fee onboarding in 4 weeks. Program staff self-service after go-live.",
  },

  "secure-file-exchange": {
    slug: "secure-file-exchange",
    code: "P08",
    tag: "Integration",
    category: "Integration · Data Exchange",
    name: "Secure File Exchange",
    headline: "Move sensitive data across boundaries without lowering the boundary.",
    description:
      "A managed SFTP + AS2 gateway with encryption at rest, key rotation, HIPAA BAA controls, and immutable audit logs. Used for state ↔ MCO EDI, state ↔ CMS submissions, CJIS data exchange, court e-filing gateways, and DoD subcontractor CUI transmittal — the same secure pipe, different partner agreements at the edges.",
    sectors: ["Health & Medicaid", "Public Safety", "Revenue & DMV", "Human Services", "Judiciary", "Defense · DoD"],
    keyStats: [
      { label: "Files / month", value: "24M+" },
      { label: "Transit encryption", value: "TLS 1.3" },
      { label: "Key rotation", value: "Auto · 90d" },
      { label: "Audit retention", value: "10 yrs" },
    ],
    modules: [
      { icon: "Server", name: "SFTP Gateway", description: "Managed SFTP endpoints with per-partner keys, IP allow-listing, and quota controls." },
      { icon: "ArrowLeftRight", name: "AS2 Gateway", description: "AS2-compliant partner exchange with MDN receipts and non-repudiation." },
      { icon: "Key", name: "Key Management", description: "Automated key rotation with partner-side coordination, HSM-backed root keys." },
      { icon: "History", name: "Audit & Chain-of-Custody", description: "Immutable log of every file transferred — who, when, hash, disposition." },
      { icon: "AlertCircle", name: "Alerting", description: "Threshold and anomaly alerts on transfer volumes, failures, and unusual patterns." },
      { icon: "FileCheck2", name: "Schema Validation", description: "Pre-transfer validation against X12, NCPDP, NIEM, and FHIR bundle schemas." },
    ],
    features: [
      "SFTP with per-partner isolated user pools",
      "AS2 with signed + encrypted MDN receipts",
      "Automated key rotation with partner coordination",
      "HSM-backed key material (FIPS 140-3)",
      "Immutable chain-of-custody log",
      "Schema validation for X12 / NCPDP / NIEM / FHIR",
      "Real-time transfer alerting",
      "10-year audit log retention",
    ],
    integrations: ["EDI translators (Sterling, Cleo, Axway)", "MMIS", "State HIEs", "MCO EDI endpoints", "CMS gateways", "CJIS interfaces", "Court e-filing systems"],
    compliance: ["HIPAA / HITECH", "FIPS 140-3 key material", "CJIS Security Policy", "NIST SP 800-53 Rev.5", "PCI-DSS v4.0", "State data-sharing agreements"],
    useCases: [
      "State ↔ MCO 837 / 835 exchange",
      "State ↔ Federal partner feeds (SSA, IRS, CMS)",
      "Law-enforcement CJIS data exchange",
      "Court electronic-filing gateways",
      "DoD subcontractor CUI transmittal",
    ],
    engagement:
      "Managed subscription with fixed-fee onboarding per partner. First partner live in 3 weeks; scale onboarding after that.",
  },
};

export const PLATFORM_SLUGS = Object.keys(PLATFORM_DETAILS);
